import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Category } from 'src/@types/Category';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// client-side-pagination-list-pages: server-side pagination + search for the
// admin Categories grid (GET /categories/list -> {total,page,pageSize,items}).
const PAGE_SIZE = 12;

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterRole] = useState('all');
  const [currentCategory, setCurrentCategory] = useState<Category | undefined>();
  const [page, setPage] = useState(1); // 1-based (MUI Pagination)
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getCategoryList = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(APIS.CATEGORIES.PAGINATED, {
        params: { page, pageSize: PAGE_SIZE, search: filterName || undefined },
      });
      const data = response.data?.data ?? response.data;
      setCategories(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load categories', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterName = (value: string) => {
    setFilterName(value);
    setPage(1);
  };

  const handleEditRow = (selected: Category) => {
    setCurrentCategory(selected);
    setModalOpen(true);
  };
  const handleDeleteRow = async (id: string) => {
    try {
      await axiosInstance.delete(APIS.CATEGORIES.PATCH(id));
      await axiosInstance.post(APIS.SUPPORT.handleDelete, {
        path: id,
        type: 'PATH',
      });
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to Delete!', { variant: 'error' });
    }
    setConfirmationOpen(true);
    getCategoryList();
  };

  return {
    modalOpen,
    setModalOpen,
    confirmationOpen,
    setConfirmationOpen,
    filterName,
    filterRole,
    currentCategory,
    setCurrentCategory,
    filteredData: categories,
    page,
    setPage,
    total,
    pageSize: PAGE_SIZE,
    loading,
    handleDeleteRow,
    handleEditRow,
    handleFilterName,
    getCategoryList,
  };
};
